/**
 * Invoice actions on the billing workbench: the decision rules, pure and testable.
 *
 * Two actions exist (Sep 2026): create an invoice for the ledger balance, and finalize a draft
 * Stripe already holds. Three ways to collect: charge the card on file now, email a pay link due
 * on a date, or leave the invoice as a draft that Stripe finalizes and charges on a chosen date
 * (Kendra, 16 Sep 2026: "the draft will have to be set to charge on 9/28"). The server route
 * applies these rules before touching Stripe; the page uses them to decide which button to show
 * and what the confirmation says.
 */

export const COLLECTION = { CHARGE: 'charge', EMAIL: 'email', SCHEDULE: 'schedule' }
export const DEFAULT_DAYS_UNTIL_DUE = 7
export const MAX_DAYS_OUT = 365
export const MIN_REASON_CHARS = 8

const DATE_RE = /^\d{4}-\d{2}-\d{2}$/
export const addDays = (ymd, n) => { const d = new Date(ymd + 'T00:00:00Z'); d.setUTCDate(d.getUTCDate() + n); return d.toISOString().slice(0, 10) }
const daysBetween = (a, b) => Math.round((Date.parse(b + 'T00:00:00Z') - Date.parse(a + 'T00:00:00Z')) / 86400000)
/** "Sep 28, 2026" for a YYYY-MM-DD, no timezone surprises. */
export const longDate = ymd => new Date(ymd + 'T12:00:00Z').toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric', timeZone: 'UTC' })

/**
 * Which invoice action a booking supports right now, if any.
 * @param {{ remainingCents: number, draftStatus?: string|null, needsBackfill?: boolean }} b
 * @returns {{ action: 'create'|'finalize'|null, why: string }}
 */
export function availableInvoiceAction({ remainingCents, draftStatus = null, needsBackfill = false }) {
  if (needsBackfill) return { action: null, why: 'Paid in Stripe but unrecorded here. Run the reconcile before invoicing.' }
  if (Number(remainingCents) <= 0) return { action: null, why: 'Nothing is owed.' }
  if (draftStatus === 'draft') return { action: 'finalize', why: 'A draft invoice already exists in Stripe. Finalize it rather than creating a second one.' }
  if (draftStatus === 'open') return { action: null, why: 'An invoice is already open and awaiting payment.' }
  return { action: 'create', why: 'No invoice exists for this balance.' }
}

/**
 * Validate and normalise the request body for either action.
 * `dueDate` (YYYY-MM-DD, business calendar) is the pay-link due date for EMAIL and the day Stripe
 * finalizes and charges for SCHEDULE. `daysUntilDue` is accepted for older callers and turned into
 * a date. `today` is YYYY-MM-DD in the business timezone.
 * @returns {{ ok: true, collection: string, dueDate: string|null, daysUntilDue: number, reason: string } | { ok: false, message: string }}
 */
export function validateInvoiceRequest({ reason, collection, dueDate, daysUntilDue, hasCard, today = new Date().toISOString().slice(0, 10) }) {
  const trimmed = String(reason || '').trim()
  if (trimmed.length < MIN_REASON_CHARS) return { ok: false, message: `A reason of at least ${MIN_REASON_CHARS} characters is required. It goes on the audit log and the client's timeline.` }
  if (![COLLECTION.CHARGE, COLLECTION.EMAIL, COLLECTION.SCHEDULE].includes(collection)) return { ok: false, message: 'Choose how to collect: charge the card on file now, email a pay link, or charge the card on a date.' }
  if (collection === COLLECTION.CHARGE && !hasCard) return { ok: false, message: 'No card on file for this client. Email a pay link instead.' }
  if (collection === COLLECTION.SCHEDULE && !hasCard) return { ok: false, message: 'No card on file for this client, so nothing can be charged on a date. Email a pay link instead.' }
  if (collection === COLLECTION.CHARGE) return { ok: true, collection, dueDate: null, daysUntilDue: 0, reason: trimmed }

  let date = typeof dueDate === 'string' && DATE_RE.test(dueDate) ? dueDate : null
  if (!date) {
    const days = Number.isFinite(Number(daysUntilDue)) ? Math.min(60, Math.max(1, Math.round(Number(daysUntilDue)))) : DEFAULT_DAYS_UNTIL_DUE
    date = addDays(today, days)
  }
  const days = daysBetween(today, date)
  if (Number.isNaN(days)) return { ok: false, message: 'Pick a date.' }
  if (collection === COLLECTION.EMAIL && days < 0) return { ok: false, message: 'The due date has already passed. Pick today or a later day.' }
  if (collection === COLLECTION.SCHEDULE && days < 1) return { ok: false, message: 'Pick a day after today for a scheduled charge. To charge today, use "Charge card on file".' }
  if (days > MAX_DAYS_OUT) return { ok: false, message: `That is more than a year out. Pick a date within ${MAX_DAYS_OUT} days.` }
  return { ok: true, collection, dueDate: date, daysUntilDue: Math.max(days, collection === COLLECTION.EMAIL ? 0 : 1), reason: trimmed }
}

/** Line item needed to bring a draft's amount_due to the ledger balance. 0 when they already agree. */
export function draftAdjustmentCents(draftAmountDueCents, remainingCents) {
  return Math.round(Number(remainingCents) - Number(draftAmountDueCents))
}

/**
 * What the nightly job does with a $99-plan final draft (Kendra, 8 Oct 2026, Jennifer Hale): the draft was sized at
 * booking as total minus every monthly payment at $99, but the last monthly charge is a partial month ($33.53), so the
 * draft ($650) fell short of the ledger ($715.47). Before finalizing, the draft is brought to the ledger balance, as
 * the Billing workbench already does. A ledger that needs backfill, or nothing owed, is left for Billing.
 * @returns { action: 'finalize', adjustCents } or { action: 'skip', reason }
 */
export function planDraftFinalize({ amountDueCents, remainingCents, needsBackfill = false }) {
  if (needsBackfill) return { action: 'skip', reason: 'ledger needs backfill: Billing to check before the final charge' }
  const remaining = Math.round(Number(remainingCents))
  if (!Number.isFinite(remaining)) return { action: 'skip', reason: 'no ledger balance for this booking' }
  if (remaining <= 0) return { action: 'skip', reason: 'nothing owed on the ledger: Billing to void the draft' }
  return { action: 'finalize', adjustCents: draftAdjustmentCents(amountDueCents, remaining) }
}

/** The plain-English steps the confirmation dialog shows before a click. */
export function describeInvoicePlan({ action, amountCents, collection, dueDate, daysUntilDue, cardLast4, adjustmentCents = 0 }) {
  const dollars = c => '$' + (Math.abs(Number(c)) / 100).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
  const card = cardLast4 ? ` ending ${cardLast4}` : ''
  const when = dueDate ? `on ${longDate(dueDate)}` : `in ${daysUntilDue} day${daysUntilDue === 1 ? '' : 's'}`
  const steps = []
  if (action === 'finalize' && adjustmentCents !== 0) {
    steps.push(adjustmentCents < 0
      ? `Credit the draft by ${dollars(adjustmentCents)} so it matches the ledger`
      : `Add ${dollars(adjustmentCents)} to the draft so it matches the ledger`)
  }
  if (collection === COLLECTION.SCHEDULE) {
    steps.push(action === 'create'
      ? `Create a Stripe invoice for ${dollars(amountCents)} and leave it as a draft`
      : `Leave the draft at ${dollars(amountCents)}`)
    steps.push(`${dueDate ? longDate(dueDate) : 'On the chosen day'}: Stripe finalizes it and charges the card on file${card}; if the charge fails, Stripe emails the client the pay link`)
    steps.push('Nothing is charged before that day; the draft can still be changed or deleted here')
  } else {
    steps.push(action === 'create'
      ? `Create and finalize a Stripe invoice for ${dollars(amountCents)}`
      : `Finalize the draft at ${dollars(amountCents)}`)
    if (collection === COLLECTION.CHARGE) {
      steps.push(`Charge the card on file${card} now; if it fails, the client gets the pay link by email`)
    } else {
      steps.push(`Email the client a pay link, due ${when}`)
    }
  }
  steps.push('Record the payment here the moment Stripe confirms it')
  steps.push('Write who did this, when, and why to the audit log')
  return steps
}

/* ------------------------------------------------------------------------------------------------
 * Phase 2: write-off and draft deletion.
 * ---------------------------------------------------------------------------------------------- */

/**
 * A write-off is staff forgiveness of money the ledger says is owed. It is recorded as a
 * price_reduction row in refunds (the same shape the client page has always used), so the
 * contract total drops and the balance follows. It never touches Stripe and never returns cash.
 * @returns {{ ok: true, amountCents: number, reason: string } | { ok: false, message: string }}
 */
export function validateWriteOff({ amountCents, remainingCents, reason, needsBackfill = false }) {
  if (needsBackfill) return { ok: false, message: 'Paid in Stripe but unrecorded here. Run the reconcile before writing anything off.' }
  const remaining = Math.round(Number(remainingCents) || 0)
  if (remaining <= 0) return { ok: false, message: 'Nothing is owed, so there is nothing to write off.' }
  const amount = Math.round(Number(amountCents))
  if (!Number.isFinite(amount) || amount <= 0) return { ok: false, message: 'Enter the amount to forgive, in dollars.' }
  if (amount > remaining) return { ok: false, message: `That is more than the ${(remaining / 100).toFixed(2)} owed. A write-off can only forgive what is owed.` }
  const trimmed = String(reason || '').trim()
  if (trimmed.length < MIN_REASON_CHARS) return { ok: false, message: `A reason of at least ${MIN_REASON_CHARS} characters is required. It goes on the audit log and the client's timeline.` }
  return { ok: true, amountCents: amount, reason: trimmed }
}

/** Payment methods staff type in by hand. Stripe money never carries one of these. */
export const HAND_RECORDED_METHODS = new Set(['other', 'cash', 'check', 'manual', 'venmo', 'zelle'])

/**
 * Can this payment row be taken off the ledger? Only a hand-recorded one (no Stripe id, a hand method). Money
 * that went through Stripe is refunded, never deleted. (21 Sep 2026, Trera: "take me out of writing sql to
 * fix anything" — the duplicate $395 on Stephanie Fykes was a delete statement until this existed.)
 */
export function availablePaymentRemoval({ payment }) {
  if (!payment) return { ok: false, message: 'Payment not found.' }
  if (payment.stripe_payment_id || payment.stripe_payment_intent_id) return { ok: false, message: 'This payment came through Stripe. Stripe money is refunded, not deleted. Use Refund.' }
  const method = String(payment.payment_method || '').toLowerCase()
  if (!HAND_RECORDED_METHODS.has(method)) return { ok: false, message: `Only payments staff typed in can be removed (this one is "${method || 'unknown'}").` }
  return { ok: true }
}

export function validatePaymentRemoval({ payment, reason }) {
  const avail = availablePaymentRemoval({ payment })
  if (!avail.ok) return avail
  const trimmed = String(reason || '').trim()
  if (trimmed.length < MIN_REASON_CHARS) return { ok: false, message: `A reason of at least ${MIN_REASON_CHARS} characters is required. It goes on the audit log with the removed payment.` }
  return { ok: true, reason: trimmed }
}

/** Steps shown before a write-off is confirmed. */
export function describeWriteOffPlan({ amountCents, remainingCents, openInvoiceCents = null, draftExists = false }) {
  const d = c => '$' + (Math.abs(Number(c)) / 100).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
  const after = Math.max(0, Math.round(Number(remainingCents)) - Math.round(Number(amountCents)))
  const steps = [
    `Record a ${d(amountCents)} price reduction on the ledger; the balance goes from ${d(remainingCents)} to ${d(after)}`,
    'Record a "Price reduced · write-off" line on the client\'s timeline. No money moves and nothing is charged or refunded',
    'Write who did this, when, and why to the audit log'
  ]
  if (openInvoiceCents !== null && openInvoiceCents > 0) steps.push(`The open Stripe invoice still shows ${d(openInvoiceCents)}. Void it in Stripe (or wait for the void action) so the client is not asked for the old amount`)
  if (draftExists && after === 0) steps.push('The draft invoice in Stripe becomes stale. Delete it with the button below once this saves')
  return steps
}

/**
 * What can be done about the invoice this booking points at. A true draft is deleted in Stripe. An invoice
 * Stripe already voided, or no longer has, cannot be deleted; only the booking's link to it is cleared, which
 * is what takes the client off the Stale drafts list (18 Sep 2026: 23 clients, all paid, all voided by hand
 * in the Stripe dashboard, stuck on the list because the site never heard). An open invoice is voided
 * instead, which is a separate action.
 * Same evening: SOME clients on the list showed no button at all. Stripe held those invoices as paid or
 * uncollectible, not void. A paid invoice the site never flagged is marked finalized here; an uncollectible
 * one is closed in Stripe already and is cleared like a void.
 * @returns {{ ok: true, mode: 'delete' | 'clear' | 'mark_paid', why: string } | { ok: false, why: string }}
 */
export function availableDraftDeletion({ draftStatus, remainingCents, hasPointer = false }) {
  if (draftStatus === 'draft') {
    if (Number(remainingCents) > 0) return { ok: true, mode: 'delete', why: 'The client still owes money. After deleting, create a fresh invoice for the balance.' }
    return { ok: true, mode: 'delete', why: 'Nothing is owed. The draft is stale and safe to delete.' }
  }
  if (draftStatus === 'void') return { ok: true, mode: 'clear', why: 'Stripe already voided this invoice. Clearing the link takes the client off the stale drafts list; nothing is sent to Stripe.' }
  if (draftStatus === 'uncollectible') return { ok: true, mode: 'clear', why: 'Stripe closed this invoice as uncollectible. Clearing the link takes the client off the stale drafts list; nothing is sent to Stripe.' }
  if (draftStatus === 'paid') return { ok: true, mode: 'mark_paid', why: 'Stripe shows this invoice paid, but the site never flagged it, which is why the client is on the stale drafts list. Marking it settled here sends nothing to Stripe.' }
  if (!draftStatus && hasPointer) return { ok: true, mode: 'clear', why: 'Stripe no longer has this invoice. Clearing the link takes the client off the stale drafts list; nothing is sent to Stripe.' }
  return { ok: false, why: draftStatus ? `The invoice is ${draftStatus}, not a draft. Only drafts can be deleted.` : 'No draft invoice to delete.' }
}

/** Steps shown before a draft deletion (or a voided-invoice link clear) is confirmed. */
export function describeDraftDeletionPlan({ draftAmountCents, remainingCents, mode = 'delete' }) {
  const d = c => '$' + (Math.abs(Number(c)) / 100).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
  const steps = mode === 'clear'
    ? [
      'Stripe already voided, closed or removed this invoice. Nothing is deleted, sent or charged',
      'Clear the invoice link on the booking so the client leaves the Stale drafts list',
      'Write who did this, when, and why to the audit log'
    ]
    : mode === 'mark_paid'
    ? [
      'Stripe already collected this invoice. Nothing is deleted, sent or charged',
      'Mark the invoice settled on the booking so the client leaves the Stale drafts list',
      'Write who did this, when, and why to the audit log'
    ]
    : [
      `Delete the ${d(draftAmountCents)} draft in Stripe. It was never sent, so the client sees nothing`,
      'Clear the invoice pointer on the booking so the workbench stops listing it as a draft',
      'Write who did this, when, and why to the audit log'
    ]
  if (Number(remainingCents) > 0) steps.push(`The client still owes ${d(remainingCents)}. The Create invoice button reappears after this`)
  return steps
}

/* ------------------------------------------------------------------------------------------------
 * Phase 3: refund through Stripe and void an open invoice.
 * ---------------------------------------------------------------------------------------------- */

export const REFUND_ADJUSTMENT = { REMOVE_CHARGE: 'cash_and_price_reduction', PRICE_STAYS: 'cash_refund' }

/**
 * A refund returns money through Stripe against one specific payment. It can never exceed what
 * that payment still holds (paid minus already refunded). The adjustment says what happens to the
 * balance: remove the charge (balance unchanged, agreed total drops) or price stays (client owes
 * the money again).
 * @returns {{ ok: true, amountCents: number, adjustment: string, reason: string } | { ok: false, message: string }}
 */
export function validateRefund({ amountCents, refundableCents, reason, adjustment, paymentIntentId }) {
  if (!paymentIntentId) return { ok: false, message: 'Pick the payment to refund from.' }
  const refundable = Math.round(Number(refundableCents) || 0)
  if (refundable <= 0) return { ok: false, message: 'That payment has already been fully refunded.' }
  const amount = Math.round(Number(amountCents))
  if (!Number.isFinite(amount) || amount <= 0) return { ok: false, message: 'Enter the amount to return, in dollars.' }
  if (amount > refundable) return { ok: false, message: `That is more than the ${(refundable / 100).toFixed(2)} still refundable on this payment.` }
  if (![REFUND_ADJUSTMENT.REMOVE_CHARGE, REFUND_ADJUSTMENT.PRICE_STAYS].includes(adjustment)) return { ok: false, message: 'Choose what happens to the balance: remove the charge, or price stays.' }
  const trimmed = String(reason || '').trim()
  if (trimmed.length < MIN_REASON_CHARS) return { ok: false, message: `A reason of at least ${MIN_REASON_CHARS} characters is required. It goes on the audit log and the client's timeline.` }
  return { ok: true, amountCents: amount, adjustment, reason: trimmed }
}

/** Steps shown before a refund is confirmed. */
export function describeRefundPlan({ amountCents, adjustment, remainingCents, cardLast4 }) {
  const d = c => '$' + (Math.abs(Number(c)) / 100).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
  const amt = Math.max(0, Math.round(Number(amountCents) || 0))
  const remaining = Math.max(0, Math.round(Number(remainingCents) || 0))
  const steps = [`Return ${d(amt)} through Stripe to the card it came from${cardLast4 ? ` (ending ${cardLast4})` : ''}. Banks take 5 to 10 days to show it`]
  if (adjustment === REFUND_ADJUSTMENT.PRICE_STAYS) {
    steps.push(`Price stays the same, so the balance goes from ${d(remaining)} to ${d(remaining + amt)}. The client owes this money again`)
  } else {
    steps.push(`Remove the charge from the agreed total, so the balance stays at ${d(remaining)}`)
  }
  steps.push('Record the refund on the client\'s timeline as soon as Stripe accepts it')
  steps.push('Write who did this, when, and why to the audit log')
  return steps
}

/** Only an open (finalized, unpaid) invoice can be voided. Drafts are deleted; paid ones are refunded. */
export function availableVoid({ invoiceStatus }) {
  if (invoiceStatus === 'open') return { ok: true, why: 'The invoice is open and unpaid. Voiding cancels it; the client can no longer pay it.' }
  if (invoiceStatus === 'draft') return { ok: false, why: 'This is a draft. Delete it instead of voiding.' }
  if (invoiceStatus === 'paid') return { ok: false, why: 'This invoice is paid. Use a refund instead.' }
  return { ok: false, why: invoiceStatus ? `The invoice is ${invoiceStatus}; nothing to void.` : 'No invoice to void.' }
}

/** Steps shown before a void is confirmed. */
export function describeVoidPlan({ invoiceAmountCents, remainingCents }) {
  const d = c => '$' + (Math.abs(Number(c)) / 100).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
  const steps = [
    `Void the ${d(invoiceAmountCents)} invoice in Stripe. Its pay link stops working and the client is not charged`,
    'Clear the invoice pointer on the booking. The ledger balance does not change',
    'Write who did this, when, and why to the audit log'
  ]
  if (Number(remainingCents) > 0) steps.push(`The client still owes ${d(remainingCents)}. Create a fresh invoice or record a write-off after this`)
  return steps
}

/* ------------------------------------------------------------------------------------------------
 * Phase 4: card-update link and requeueing a dead webhook event.
 * ---------------------------------------------------------------------------------------------- */

export const CARD_LINK_DELIVERY = { COPY: 'copy', TEXT: 'text' }

/** The text Kendra sends with a card-update link. Short, names the business, no marketing. */
export function cardLinkMessage({ firstName, url }) {
  const who = String(firstName || '').trim()
  return `${who ? `Hi ${who}, ` : 'Hi, '}this is Love & Photos. Please update the card on file for your booking here: ${url} (secure Stripe page, takes a minute). Reply STOP to opt out.`
}

/** Validate a card-link request. Texting needs a phone with consent. */
export function validateCardLinkRequest({ reason, delivery, canText }) {
  const trimmed = String(reason || '').trim()
  if (trimmed.length < MIN_REASON_CHARS) return { ok: false, message: `A reason of at least ${MIN_REASON_CHARS} characters is required. It goes on the audit log.` }
  if (![CARD_LINK_DELIVERY.COPY, CARD_LINK_DELIVERY.TEXT].includes(delivery)) return { ok: false, message: 'Choose how to deliver the link: copy it, or text it.' }
  if (delivery === CARD_LINK_DELIVERY.TEXT && !canText) return { ok: false, message: 'This client has no phone with text consent. Copy the link instead.' }
  return { ok: true, reason: trimmed, delivery }
}

/** Steps shown before a card link is created. */
export function describeCardLinkPlan({ delivery, hasSubscription }) {
  const steps = ['Create a secure Stripe page where the client enters a new card. The link works for 24 hours']
  steps.push(delivery === CARD_LINK_DELIVERY.TEXT ? 'Text the link to the client from the Love & Photos number' : 'Show the link here so you can copy it into an email or message')
  steps.push(hasSubscription ? 'When they finish, the new card becomes the default for the customer and the monthly subscription' : 'When they finish, the new card becomes the default card on file')
  steps.push('Write who did this, when, and why to the audit log')
  return steps
}

/** Only parked or failed events with a stored payload can be requeued. */
export function availableRequeue({ status, hasPayload }) {
  if (!hasPayload) return { ok: false, why: 'This event has no stored payload (it predates the queue). Stripe can resend it from the dashboard.' }
  if (status === 'dead' || status === 'failed') return { ok: true, why: status === 'dead' ? 'Parked after 5 failed attempts. Requeue once the cause is fixed.' : 'Waiting for its next retry. Requeue runs it now.' }
  return { ok: false, why: `The event is ${status}; nothing to requeue.` }
}
